import {
  getContractManagementContract,
  getContractManagementContractFile,
  getVaultFileMetadata,
} from '@/api';
import { logger } from '@/utils/logger';

/**
 * What the chat can be pointed at. Mirrors `AskAiSelection` in `utils/askAi.ts`
 * — vault directories are absent on purpose: the backend resolves references to
 * concrete file revisions and has no field for a folder.
 */
export type ChatContextType = 'file' | 'contract' | 'contractFile';

/**
 * The `<selected_documents>` marker prefix the backend writes for each type, so
 * a message composed here and one restored from history key the same way.
 */
const KEY_PREFIX: Record<ChatContextType, string> = {
  file: 'file-',
  contract: 'contract-',
  contractFile: 'cfile-',
};

export function contextKey(type: ChatContextType, uid: string): string {
  return `${KEY_PREFIX[type]}${uid}`;
}

/** A readable stand-in until the real name arrives — never a raw uid on its own. */
export function fallbackContextName(type: ChatContextType, uid: string): string {
  if (type === 'contract') return `Contract ${uid.slice(0, 8)}`;
  if (type === 'contractFile') return `Contract file ${uid.slice(0, 8)}`;
  return `File ${uid.slice(0, 8)}`;
}

/** The kind label beside a reference — what it is, rather than which one. */
export function fallbackContextKind(type: ChatContextType): string {
  if (type === 'contract') return 'Contract';
  if (type === 'contractFile') return 'Contract file';
  return 'File';
}

/** Each kind's home page. Returns a path; navigating is the host's job. */
export function contextItemRoute(type: ChatContextType, uid: string): string {
  if (type === 'contract') return `/contract-management/contracts/${uid}`;
  if (type === 'contractFile') return `/contract-management/contract-files/${uid}`;
  // Vault files land on the versions view — the file's page in the main vault.
  return `/vault/file/${uid}/versions`;
}

export interface ReferencedItem {
  type: ChatContextType;
  uid: string;
  key: string;
}

/**
 * The backend's `referenced_items` kind names, onto ours. Values are optional
 * because the lookup key comes from untyped metadata — an unknown kind is a
 * miss, not a type error.
 */
const REFERENCED_KIND_TYPES: Record<string, ChatContextType | undefined> = {
  file: 'file',
  contract: 'contract',
  'contract file': 'contractFile',
};

/**
 * Every document a thread touched, from the `referenced_items` list the backend
 * keeps in `thread_metadata`. That bag is untyped (`Record<string, unknown>`),
 * so this stays defensive: anything malformed is skipped rather than thrown on,
 * and a thread that predates the field simply reports nothing.
 */
export function parseReferencedItems(threadMetadata: Record<string, unknown>): ReferencedItem[] {
  const raw = threadMetadata.referenced_items;
  if (!Array.isArray(raw)) return [];

  const items: ReferencedItem[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { kind, uid } = entry as { kind?: unknown; uid?: unknown };
    if (typeof kind !== 'string' || typeof uid !== 'string' || !uid) continue;
    const type = REFERENCED_KIND_TYPES[kind];
    if (!type) continue;
    const key = contextKey(type, uid);
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ type, uid, key });
  }
  return items;
}

/**
 * The display name for one referenced item. Returns null when nothing
 * human-readable exists (or the lookup fails) — the caller keeps its fallback
 * rather than showing a uid.
 */
export async function fetchContextName(type: ChatContextType, uid: string): Promise<string | null> {
  try {
    if (type === 'contract') {
      const response = await getContractManagementContract({ path: { uid } });
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      return response.data?.name.trim() || null;
    }
    if (type === 'contractFile') {
      const response = await getContractManagementContractFile({ path: { uid } });
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      return response.data?.vault_file?.current_revision?.file_name.trim() || null;
    }
    const response = await getVaultFileMetadata({ path: { file_uid: uid } });
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
    return response.data?.current_revision?.file_name.trim() || null;
  } catch (error) {
    // A missing name is cosmetic — the uid still rides into the request.
    logger.error(`Failed to resolve the name of ${type} ${uid}:`, error);
    return null;
  }
}

/** Comma-separated uid list from a route query value, as `askAiRoute` writes it. */
export function parseUidList(value: unknown): string[] {
  const raw =
    typeof value === 'string'
      ? value
      : Array.isArray(value)
        ? value.find((item): item is string => typeof item === 'string')
        : null;
  if (!raw) return [];
  return raw
    .split(',')
    .map(uid => uid.trim())
    .filter(Boolean);
}
